<template>
    <v-card class="defaultCard pa-3" color="bgtopology" flat>
        <!-- PESTAÑAS PRINCIPALES CON DESPLAZAMIENTO FLUIDO EN MÓVIL -->
        <v-tabs
            v-model="activeTab"
            color="primary"
            class="mb-3"
            align-tabs="start"
            show-arrows
        >
            <v-tab value="grid">
                <template #default>
                    <v-icon start size="small">mdi-grid</v-icon>
                    Tarjeta de Latencias
                </template>
            </v-tab>
            <v-tab value="topology">
                <template #default>
                    <v-icon start size="small">mdi-hub</v-icon>
                    Topología por Zonas
                </template>
            </v-tab>
            <v-tab value="mesh">
                <template #default>
                    <v-icon start size="small">mdi-sitemap</v-icon>
                    Vista de Mapa
                </template>
            </v-tab>
        </v-tabs>

        <!-- ENCABEZADO GLOBAL ADAPTABLE (RESPONSIVE) -->
        <v-row class="ma-0 align-center justify-space-between mb-3 ga-2">
            <!-- TÍTULO Y BOTÓN REFRESH -->
            <v-col cols="12" sm="auto" class="pa-0 d-flex align-center ga-2">
                <span class="titlePages text-truncate">{{ currentViewTitle }}</span>
                <v-btn
                    icon="mdi-refresh"
                    variant="tonal"
                    color="primary"
                    size="x-small"
                    :loading="loadingRefresh"
                    @click="getAllLatencies(true)"
                >
                    <v-icon size="16" :class="{ 'spin-icon': loadingRefresh }">mdi-refresh</v-icon>
                    <v-tooltip activator="parent" location="bottom">Actualizar datos</v-tooltip>
                </v-btn>
            </v-col>

            <!-- BUSCADOR Y CONTROLES ADAPTABLES -->
            <v-col cols="12" sm="auto" class="pa-0 d-flex flex-wrap align-center justify-end ga-2">
                <!-- BUSCADOR -->
                <v-text-field
                    v-model="searchQuery"
                    density="compact"
                    variant="outlined"
                    hide-details
                    placeholder="Buscar por nombre, IP o tipo..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    class="flex-grow-1 flex-sm-grow-0"
                    style="min-width: 200px; max-width: 100%;"
                ></v-text-field>

                <!-- SELECTOR DE ORDENAMIENTO (PESTAÑA GRID) -->
                <v-select
                    v-if="activeTab === 'grid'"
                    v-model="sortBy"
                    :items="sortOptions"
                    item-title="title"
                    item-value="value"
                    density="compact"
                    variant="outlined"
                    hide-details
                    class="flex-grow-1 flex-sm-grow-0"
                    style="min-width: 180px; max-width: 100%;"
                    prepend-inner-icon="mdi-sort"
                ></v-select>

                <!-- BOTONES DE SUBTIPO (PESTAÑAS TOPOLOGÍA Y MALLA) -->
                <v-btn-toggle
                    v-else
                    v-model="selectedSubtype"
                    color="primary"
                    mandatory
                    density="compact"
                    variant="outlined"
                    class="flex-grow-1 flex-sm-grow-0 custom-responsive-toggle"
                >
                    <v-btn value="distribución" class="flex-grow-1">
                        <v-icon start size="small" class="hidden-xs-only">mdi-routes</v-icon>
                        <span>Distribución</span>
                        <v-chip size="x-small" color="primary" class="ml-1 px-1" style="height: 14px;">
                            {{ countBySubtype('distribución') }}
                        </v-chip>
                    </v-btn>

                    <v-btn value="acceso" class="flex-grow-1">
                        <v-icon start size="small" class="hidden-xs-only">mdi-access-point</v-icon>
                        <span>Acceso</span>
                        <v-chip size="x-small" color="primary" class="ml-1 px-1" style="height: 14px;">
                            {{ countBySubtype('acceso') }}
                        </v-chip>
                    </v-btn>

                    <v-btn value="cliente" class="flex-grow-1">
                        <v-icon start size="small" class="hidden-xs-only">mdi-account-network</v-icon>
                        <span>Clientes</span>
                        <v-chip size="x-small" color="primary" class="ml-1 px-1" style="height: 14px;">
                            {{ countBySubtype('cliente') }}
                        </v-chip>
                    </v-btn>
                </v-btn-toggle>
            </v-col>
        </v-row>

        <v-window v-model="activeTab">
            <v-window-item value="grid">
                <v-sheet v-if="showCircle" class="text-center py-8" color="transparent"><Prog_circular /></v-sheet>
                <v-row v-else-if="showError" class="ma-0 py-4 justify-center"><h3 class="text-grey-lighten-1">{{ t('noInformation') }}</h3></v-row>
                <LatencyGrid v-else :filtered-groups="filteredGroups" :get-latency-color="getLatencyColor" :get-operability-percentages="getOperabilityPercentages" :format-short-date="formatShortDate" @select-device="showDialogWithType" />
            </v-window-item>

            <v-window-item value="topology">
                <CircularTopology :items="filteredDataBySubtype" :get-operability-percentages="getOperabilityPercentages" :format-short-date="formatShortDate" @select-device="showDialogWithType" />
            </v-window-item>

            <v-window-item value="mesh">
                <MeshTopology :items="filteredDataBySubtype" :get-latency-color="getLatencyColor" :get-operability-percentages="getOperabilityPercentages" :format-short-date="formatShortDate" @select-device="showDialogWithType" />
            </v-window-item>
        </v-window>

        <v-dialog v-model="dialog" width="85%">
            <DialogComponent v-if="dialog" :ip="selectedIp" :typeSel="selectedType" :name="selectedName" :model="selectedModel" @dialog="dialog = false" />             
        </v-dialog>
    </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
// import { useStore } from 'vuex';

import LatencyGrid from '@/components/SmartLink/topology/LatencyGrid.vue';
import CircularTopology from '@/components/SmartLink/topology/CircularTopology.vue';
import MeshTopology from '@/components/SmartLink/topology/MeshTopology.vue';
import DialogComponent from '@/components/SmartLink/dialog/dialogComponent.vue';
import Prog_circular from '@/components/PrincipalComponents/Prog_circular.vue';

// import { LABELS } from '@/constants/labels';
import { getAllLatencyData } from '@/services/Smartlink/KPI';
import { getOperabilityLastDayTopology } from '@/services/Smartlink/Topology';
import { LATENCY } from '@/interfaces/SmartLink/Topology';
import { OPERABILITY } from '@/interfaces/SmartLink/Equipos';
import { getDeviceModel, extractNumberFromName } from '@/utils/SmartLink/device';
import { getRajantGPS } from "@/services/Smartlink/Drive";
import { getAllDataLatLngSM } from "@/services/Smartlink/Map";

const { t } = useI18n();
// const store = useStore();

const activeTab = ref('grid');
const selectedSubtype = ref('distribución');
const rawData = ref<LATENCY[]>([]);
const getAllIEOByIP = ref<OPERABILITY[]>([]);
const searchQuery = ref('');
const showError = ref(false);
const showCircle = ref(true);
const loadingRefresh = ref(false);
const sortBy = ref('latency_desc');

const dialog = ref(false);
const selectedIp = ref('');
const selectedName = ref('');
const selectedType = ref('');
const selectedModel = ref('');

const sortOptions = [
    { title: 'Mayor Latencia', value: 'latency_desc' },
    { title: 'Menor Latencia', value: 'latency_asc' },
    { title: 'Nombre (A - Z)', value: 'name_asc' },
    { title: 'IP', value: 'ip_asc' }
];

let timerId: ReturnType<typeof setInterval> | null = null;

const currentViewTitle = computed(() => {
    if (activeTab.value === 'grid') return t('title.allLatency') || 'Monitor de Latencias';
    if (activeTab.value === 'topology') return 'Topología por Zonas de Latencia';
    return 'Vista de Mapa';
});

function getLatencyColor(latencia: number): string {
    if (latencia < 0) return '#9E9E9E';   
    if (latencia <= 100) return '#00E676'; 
    if (latencia <= 500) return '#FFEA00'; 
    return '#FF3D00';                      
}

function getOperabilityPercentages(ip: string) {
    const item = getAllIEOByIP.value.find(op => op.ip === ip);
    if (!item) return { ok: 0, alert: 0, alarm: 0, down: 100 };
    const total = (item.ok || 0) + (item.alert || 0) + (item.alarm || 0) + (item.down || 0);
    if (total === 0) return { ok: 0, alert: 0, alarm: 0, down: 100 };
    return { ok: ((item.ok || 0) / total) * 100, alert: ((item.alert || 0) / total) * 100, alarm: ((item.alarm || 0) / total) * 100, down: ((item.down || 0) / total) * 100 };
}

async function getAllLatencies(isManualRefresh = false) {
    if (isManualRefresh) loadingRefresh.value = true;

    // 1. Cargar Operatividad
    const opRes = await getOperabilityLastDayTopology();
    if (opRes.status === 200) getAllIEOByIP.value = opRes.data;

    // 2. Cargar Latencias, GPS Rajant y GPS SM en paralelo
    const [responseLat, responseGps, responseSm] = await Promise.all([
        getAllLatencyData(),
        getRajantGPS(),
        getAllDataLatLngSM()
    ]);

    showError.value = !responseLat.showGraphs;
    showCircle.value = false;
    loadingRefresh.value = false;

    if (responseLat.status === 200) {
        const latencyData = responseLat.data || [];
        const gpsRajantData = responseGps.status === 200 ? responseGps.data : [];
        const smData = responseSm.status === 200 ? responseSm.data : [];

        // Mapa de coordenadas por IP directa de Rajant
        const rajantGpsMap = new Map<string, { latitud: number; longitud: number }>();
        
        // MAPA EXCLUSIVO DE ACCESO AP MÓVIL POR IDENTIFICADOR NUMÉRICO
        const apMovilGpsMapById = new Map<string, { latitud: number; longitud: number }>();

        gpsRajantData.forEach((g: any) => {
            if (g.ip && g.latitud && g.longitud) {
                rajantGpsMap.set(g.ip, { latitud: g.latitud, longitud: g.longitud });

                // DISCRIMINACIÓN ESTRICТА: Solo Acceso AP Móvil
                const isAcceso = g.subtipo?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") === "acceso";
                const isApMovil = g.tipo?.toLowerCase().replace(/\s+/g, "") === "apmovil";

                if (isAcceso && isApMovil) {
                    const idNum = extractNumberFromName(g.name, "RAJANT");
                    if (idNum) {
                        apMovilGpsMapById.set(idNum, { latitud: g.latitud, longitud: g.longitud });
                    }
                }
            }
        });

        // Mapa de datos SM por IP
        const smGpsMap = new Map<string, any>();
        smData.forEach((s: any) => {
            if (s.ip) smGpsMap.set(s.ip, s);
        });

        const extractGpsIp = (anotacion: any): string => {
            if (!anotacion) return '';
            try {
                const parsed = typeof anotacion === 'string' ? JSON.parse(anotacion) : anotacion;
                return parsed?.gps || '';
            } catch {
                const match = String(anotacion).match(/gps:\s*["']?([^"',}]+)["']?/);
                return match ? match[1].trim() : '';
            }
        };

        // 3. Fusionar datos de Latencia con Coordenadas
        rawData.value = latencyData.map((item: LATENCY) => {
            let latitud = 0;
            let longitud = 0;

            const isSubtipoDistribucion = item.subtipo?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") === 'distribución';
            const smInfo = smGpsMap.get(item.ip);

            // Si es PMP-SM / Distribución
            if (isSubtipoDistribucion || smInfo) {
                // Opción A: Probar con la IP especificada dentro de anotación.gps
                const targetGpsIp = extractGpsIp(item.anotacion) || extractGpsIp(smInfo?.anotacion);

                if (targetGpsIp && rajantGpsMap.has(targetGpsIp)) {
                    const linkedGps = rajantGpsMap.get(targetGpsIp)!;
                    latitud = linkedGps.latitud;
                    longitud = linkedGps.longitud;
                } else {
                    // Opción B: Si no vino la IP en la anotación, asociar por identificador con un ACCESO AP MÓVIL
                    const smIdNum = extractNumberFromName(item.name, "PMPSM");
                    if (smIdNum && apMovilGpsMapById.has(smIdNum)) {
                        const linkedGps = apMovilGpsMapById.get(smIdNum)!;
                        latitud = linkedGps.latitud;
                        longitud = linkedGps.longitud;
                    }
                }
            } else {
                // Para Acceso / Clientes con GPS directo
                const rajantGps = rajantGpsMap.get(item.ip);
                if (rajantGps) {
                    latitud = rajantGps.latitud;
                    longitud = rajantGps.longitud;
                }
            }

            const gpsInfo = gpsRajantData.find((g: any) => g.ip === item.ip);

            return {
                ...item,
                latitud,
                longitud,
                tipo: item.tipo || gpsInfo?.tipo || smInfo?.tipo || '',
                subtipo: item.subtipo || gpsInfo?.subtipo || ''
            };
        });
    }
}

function showDialogWithType(item: LATENCY) {
    selectedIp.value = item.ip;
    selectedName.value = item.name;
    selectedType.value = item.tipo;
    selectedModel.value = getDeviceModel(item.anotacion);
    dialog.value = true;
}

function formatShortDate(dateStr?: string) {
    if (!dateStr) return 'N/A';
    const parts = dateStr.trim().split(' ');
    return parts.length < 2 ? dateStr : `${parts[0]} ${parts[1].substring(0, 5)}`;
}

const filteredDataBySubtype = computed(() => {
    // Si searchQuery es null o undefined, se convierte en ""
    const query = (searchQuery.value || '').toLowerCase().trim();

    return rawData.value.filter(item => {
        if (!item.subtipo) return false;

        const normalizedSubtype = item.subtipo.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const targetSubtype = selectedSubtype.value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const matchesSubtype = normalizedSubtype === targetSubtype;

        if (!query) return matchesSubtype;

        const matchesQuery = 
            item.name?.toLowerCase().includes(query) ||
            item.ip?.toLowerCase().includes(query) ||
            item.tipo?.toLowerCase().includes(query);

        return matchesSubtype && matchesQuery;
    });
});

const filteredGroups = computed(() => {
    // Si searchQuery es null o undefined, se convierte en ""
    const query = (searchQuery.value || '').toLowerCase().trim();

    // 1. Filtrar por búsqueda
    const filtered = rawData.value.filter((item: LATENCY) => {
        if (!query) return true;
        return (
            item.name?.toLowerCase().includes(query) ||
            item.ip?.toLowerCase().includes(query) ||
            item.tipo?.toLowerCase().includes(query)
        );
    });

    // 2. Ordenar arreglo según la opción elegida en sortBy
    filtered.sort((a, b) => {
        if (sortBy.value === 'latency_desc') {
            const latA = a.latencia < 0 ? 999999 : a.latencia;
            const latB = b.latencia < 0 ? 999999 : b.latencia;
            return latB - latA;
        } 
        if (sortBy.value === 'latency_asc') {
            const latA = a.latencia < 0 ? 999999 : a.latencia;
            const latB = b.latencia < 0 ? 999999 : b.latencia;
            return latA - latB;
        }
        if (sortBy.value === 'name_asc') {
            return (a.name || '').localeCompare(b.name || '');
        }
        if (sortBy.value === 'ip_asc') {
            return (a.ip || '').localeCompare(b.ip || '');
        }
        return 0;
    });

    // 3. Agrupar por subtipos
    const categories = [
        { key: 'distribución', title: 'Distribución' },
        { key: 'acceso', title: 'Acceso' },
        { key: 'cliente', title: 'Clientes' }
    ];

    return categories.map(cat => ({
        subtipo: cat.key,
        title: cat.title,
        items: filtered.filter((item: LATENCY) => {
            if (!item.subtipo) return false;
            return item.subtipo.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") ===
                   cat.key.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        })
    }));
});

function countBySubtype(subtypeKey: string): number {
    if (!rawData.value || rawData.value.length === 0) return 0;

    // Normalizar la clave buscada (eliminar tildes, minúsculas y espacios)
    const targetKey = subtypeKey
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();

    return rawData.value.filter(item => {
        if (!item.subtipo) return false;

        // Normalizar el subtipo proveniente del objeto
        const itemSubtype = item.subtipo
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .trim();

        // Comparación flexible (soporta singular/plural y coincidencia exacta)
        return (
            itemSubtype === targetKey ||
            itemSubtype.startsWith(targetKey) ||
            targetKey.startsWith(itemSubtype)
        );
    }).length;
}

onMounted(() => {
    getAllLatencies();
    timerId = setInterval(() => getAllLatencies(), 60000);
});
onUnmounted(() => timerId && clearInterval(timerId));
</script>

<style>
.topology-wrapper {
    position: relative;
    width: 100%;
    height: 75vh;
    background-color: #06090e;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.05);
}

.topology-wrapper:fullscreen {
    width: 100vw !important;
    height: 100vh !important;
    border-radius: 0 !important;
}

.network-container {
    width: 100%;
    height: 100%;
}

.fullscreen-btn {
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 10;
    background-color: rgba(15, 23, 42, 0.8) !important;
    backdrop-filter: blur(4px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #FFFFFF !important;
}

.fullscreen-btn:hover {
    background-color: rgba(30, 41, 59, 0.95) !important;
}

/* --- LEYENDAS Y ETIQUETAS --- */
.topology-legend,
.card-grid-legend {
    display: none !important;
}

.legend-title {
    color: #94A3B8;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.legend-text {
    font-size: 0.68rem;
    color: #E2E8F0;
    font-family: monospace;
}

.legend-color-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
}

/* --- TARJETAS DE LA VISTA DE GRID (LatencyGrid.vue) --- */
.card-device-ultracompact {
    background-color: #121824;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 8px !important;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
    height: 100%;
}

.card-device-ultracompact:hover {
    border-color: rgba(255, 255, 255, 0.35);
    background-color: #1a2232;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.7) !important;
}

.device-name { 
    font-size: 0.70rem; 
}

.device-model { 
    font-size: 0.60rem; 
}

.ip-text { 
    font-family: monospace; 
    font-size: 0.62rem; 
    color: #B0BEC5; 
}

.latency-badge { 
    font-size: 0.62rem; 
    padding: 0 4px; 
    border-radius: 4px; 
    background-color: rgba(0, 0, 0, 0.5); 
}

/* --- BARRA DE OPERATIVIDAD EN TARJETAS --- */
.operability-bar-wrapper {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
    overflow: hidden;
}

.operability-stacked-bar {
    display: flex;
    width: 100%;
    height: 4px;
}

.bar-segment {
    height: 100%;
    transition: width 0.3s ease;
}

/* --- ANIMACIONES --- */
.spin-icon { 
    animation: spin 1s linear infinite; 
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
</style>